/**
 * Store Service
 *
 * API service for store status management.
 */

import { apiClient } from './api.service';
import type {
  Store,
  StoreQueryParams,
  PaginatedStores,
  ApproveStoreDto,
  SuspendStoreDto,
  CloseStoreDto,
  TemporaryCloseStoreDto,
  ReactivateStoreDto,
  UpdateStoreDto,
  CreateStoreDto,
  StoreMedia,
} from '@/types/store.types';

// ============================================================================
// SERVICE
// ============================================================================

export const storeService = {
  /**
   * List stores with search/filter (admin)
   */
  async getAll(
    params?: StoreQueryParams,
    opts: { signal?: AbortSignal } = {},
  ): Promise<PaginatedStores> {
    const searchParams = new URLSearchParams();

    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.status) searchParams.set('status', params.status);
    if (params?.countryId) searchParams.set('countryId', params.countryId);
    if (params?.search) searchParams.set('search', params.search);

    const query = searchParams.toString();
    const endpoint = query ? `/stores?${query}` : '/stores';

    const response = await apiClient.get<PaginatedStores>(endpoint, {
      signal: opts.signal,
    });
    return response.data;
  },

  /**
   * Create a new store for a company
   */
  async create(companyId: string, data: CreateStoreDto): Promise<Store> {
    const response = await apiClient.post<Store>(`/companies/${companyId}/stores`, data);
    return response.data;
  },

  /**
   * Get store by ID
   */
  async getById(
    id: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<Store> {
    const response = await apiClient.get<Store>(`/stores/${id}`, {
      signal: opts.signal,
    });
    return response.data;
  },

  /**
   * Update a store
   */
  async update(id: string, data: UpdateStoreDto): Promise<Store> {
    const response = await apiClient.patch<Store>(`/stores/${id}`, data);
    return response.data;
  },

  /**
   * Approve a store (PENDING_APPROVAL → CONFIGURING)
   */
  async approve(id: string, data: ApproveStoreDto = {}): Promise<Store> {
    const response = await apiClient.post<Store>(`/stores/${id}/approve`, data);
    return response.data;
  },

  /**
   * Suspend a store (ACTIVE -> SUSPENDED)
   */
  async suspend(id: string, data: SuspendStoreDto): Promise<Store> {
    const response = await apiClient.post<Store>(`/stores/${id}/suspend`, data);
    return response.data;
  },

  /**
   * Temporarily close a store
   */
  async temporaryClose(id: string, data: TemporaryCloseStoreDto): Promise<Store> {
    const response = await apiClient.post<Store>(`/stores/${id}/temporary-close`, data);
    return response.data;
  },

  /**
   * Permanently close a store
   */
  async close(id: string, data: CloseStoreDto): Promise<Store> {
    const response = await apiClient.post<Store>(`/stores/${id}/close`, data);
    return response.data;
  },

  /**
   * Reactivate a suspended or temporarily closed store
   */
  async reactivate(id: string, data: ReactivateStoreDto = {}): Promise<Store> {
    const response = await apiClient.post<Store>(`/stores/${id}/reactivate`, data);
    return response.data;
  },

  /**
   * Get store documents
   */
  async getDocuments(
    id: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<StoreMedia[]> {
    const response = await apiClient.get<StoreMedia[]>(
      `/stores/${id}/documents`,
      { signal: opts.signal },
    );
    return response.data;
  },

  /**
   * Add document to store
   */
  async addDocument(storeId: string, mediaId: string): Promise<StoreMedia> {
    const response = await apiClient.post<StoreMedia>(`/stores/${storeId}/documents`, { mediaId });
    return response.data;
  },

  /**
   * Remove document from store
   */
  async removeDocument(storeId: string, mediaId: string): Promise<{ success: boolean }> {
    const response = await apiClient.post<{ success: boolean }>(`/stores/${storeId}/documents/${mediaId}/remove`);
    return response.data;
  },
};

export default storeService;
